import Coupon from 'common/components/entities/OfferPriceNew/Coupon'
import LimitedSubscriptionPlan from 'common/components/entities/OfferPriceNew/PricePlan/LimitedSubscriptionPlan'
import SubscriptionPlan from 'common/components/entities/OfferPriceNew/PricePlan/SubscriptionPlan'
import Total from 'common/components/entities/OfferPriceNew/Total'
import CurrencyBadgeUi from 'common/components/entities/OfferPriceNew/ui/CurrencyBadgeUi'
import PricePlanAmountUi from 'common/components/entities/OfferPriceNew/ui/PricePlanAmountUi'
import PricePlanDescriptionUi from 'common/components/entities/OfferPriceNew/ui/PricePlanDescriptionUi'
import PricePlanItemUi from 'common/components/entities/OfferPriceNew/ui/PricePlanItemUi'
import PricePlanLabelUi from 'common/components/entities/OfferPriceNew/ui/PricePlanLabelUi'
import PricePlanNameUi from 'common/components/entities/OfferPriceNew/ui/PricePlanNameUi'
import PricePlanUi from 'common/components/entities/OfferPriceNew/ui/PricePlanUi'
import Vat from 'common/components/entities/OfferPriceNew/Vat'
import { PricePlanTypeEnum } from 'common/enums/PricePlanTypeEnum'
import { Coupon as CouponType } from 'common/types/CouponType'
import { PricePlan as PricePlanType } from 'common/types/OfferInterface'
import React from 'react'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import {
  centsToEuro,
  getReadablePrice,
} from 'src/shared/utils/price-calculator'

export interface PricePlanProps {
  plan: PricePlanType
  pricePlanPricing: CalculatePriceResponse
  isActive?: boolean
  coupon?: CouponType
  quantity?: number
  customDescription?: string
  togglePricePlan?: (id: number) => void
  locale: string
  nameFontFamily?: string
  mobileNameFontFamily?: string
  nameFontSize: number
  mobileNameFontSize?: number
  nameFontStyle?: string
  mobileNameFontStyle?: string
  nameFontWeight?: string
  mobileNameFontWeight?: string
  nameLineHeight: number
  mobileNameLineHeight?: number
  nameColor: string
  mobileNameColor?: string
  amountFontFamily?: string
  mobileAmountFontFamily?: string
  amountFontSize: number
  mobileAmountFontSize?: number
  amountFontStyle?: string
  mobileAmountFontStyle?: string
  amountFontWeight?: string
  mobileAmountFontWeight?: string
  amountLineHeight: number
  mobileAmountLineHeight?: number
  amountColor: string
  mobileAmountColor?: string
  descriptionFontFamily?: string
  mobileDescriptionFontFamily?: string
  descriptionFontSize: number
  mobileDescriptionFontSize?: number
  descriptionFontStyle?: string
  mobileDescriptionFontStyle?: string
  descriptionFontWeight?: string
  mobileDescriptionFontWeight?: string
  descriptionLineHeight: number
  mobileDescriptionLineHeight?: number
  descriptionColor: string
  mobileDescriptionColor?: string
  dataTestId: string
}

const PricePlan = ({
  plan,
  isActive,
  coupon,
  quantity = 1,
  togglePricePlan,
  pricePlanPricing,
  customDescription,
  locale,
  nameFontSize,
  mobileNameFontSize,
  nameFontStyle,
  mobileNameFontStyle,
  nameFontWeight,
  mobileNameFontWeight,
  nameFontFamily,
  mobileNameFontFamily,
  nameLineHeight,
  mobileNameLineHeight,
  nameColor,
  mobileNameColor,
  amountFontSize,
  mobileAmountFontSize,
  amountFontStyle,
  mobileAmountFontStyle,
  amountFontWeight,
  mobileAmountFontWeight,
  amountFontFamily,
  mobileAmountFontFamily,
  amountLineHeight,
  mobileAmountLineHeight,
  amountColor,
  mobileAmountColor,
  descriptionFontSize,
  mobileDescriptionFontSize,
  descriptionFontStyle,
  mobileDescriptionFontStyle,
  descriptionFontWeight,
  mobileDescriptionFontWeight,
  descriptionFontFamily,
  mobileDescriptionFontFamily,
  descriptionLineHeight,
  mobileDescriptionLineHeight,
  descriptionColor,
  mobileDescriptionColor,
  dataTestId,
}: PricePlanProps) => {
  const needTotal = pricePlanPricing.tax.amount > 0 || !!coupon

  return (
    <PricePlanUi>
      <PricePlanItemUi skipLeftOffset>
        <PricePlanNameUi
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
          data-test-id={dataTestId}
        >
          {!togglePricePlan ? (
            plan.name
          ) : (
            <PricePlanLabelUi>
              <input
                type="radio"
                checked={isActive}
                onChange={() => togglePricePlan(plan.id)}
              />{' '}
              <div>{plan.name}</div>
            </PricePlanLabelUi>
          )}
        </PricePlanNameUi>
        <PricePlanAmountUi
          fontSize={amountFontSize}
          mobileFontSize={mobileAmountFontSize}
          fontStyle={amountFontStyle}
          mobileFontStyle={mobileAmountFontStyle}
          fontWeight={amountFontWeight}
          mobileFontWeight={mobileAmountFontWeight}
          fontFamily={amountFontFamily}
          mobileFontFamily={mobileAmountFontFamily}
          lineHeight={amountLineHeight}
          mobileLineHeight={mobileAmountLineHeight}
          color={amountColor}
          mobileColor={mobileAmountColor}
        >
          <CurrencyBadgeUi color={amountColor} mobileColor={mobileAmountColor}>
            {plan.currency}
          </CurrencyBadgeUi>
          <ReadablePricePlanAmount
            plan={plan}
            amount={pricePlanPricing.originalAmount}
            locale={locale}
          />
        </PricePlanAmountUi>
      </PricePlanItemUi>
      {customDescription && (
        <PricePlanItemUi needBottomOffset={isActive && needTotal}>
          <PricePlanDescriptionUi
            fontSize={descriptionFontSize}
            mobileFontSize={mobileDescriptionFontSize}
            fontStyle={descriptionFontStyle}
            mobileFontStyle={mobileDescriptionFontStyle}
            fontWeight={descriptionFontWeight}
            mobileFontWeight={mobileDescriptionFontWeight}
            fontFamily={descriptionFontFamily}
            mobileFontFamily={mobileDescriptionFontFamily}
            lineHeight={descriptionLineHeight}
            mobileLineHeight={mobileDescriptionLineHeight}
            color={descriptionColor}
            mobileColor={mobileDescriptionColor}
          >
            {customDescription}
          </PricePlanDescriptionUi>
        </PricePlanItemUi>
      )}
      {isActive && (
        <>
          {coupon && (
            <Coupon
              amountFontSize={amountFontSize}
              mobileAmountFontSize={mobileAmountFontSize}
              amountFontStyle={amountFontStyle}
              mobileAmountFontStyle={mobileAmountFontStyle}
              amountFontWeight={amountFontWeight}
              mobileAmountFontWeight={mobileAmountFontWeight}
              amountFontFamily={amountFontFamily}
              mobileAmountFontFamily={mobileAmountFontFamily}
              amountLineHeight={amountLineHeight}
              mobileAmountLineHeight={mobileAmountLineHeight}
              amountColor={amountColor}
              mobileAmountColor={mobileAmountColor}
              fontSize={nameFontSize}
              mobileFontSize={mobileNameFontSize}
              fontStyle={nameFontStyle}
              mobileFontStyle={mobileNameFontStyle}
              fontWeight={nameFontWeight}
              mobileFontWeight={mobileNameFontWeight}
              fontFamily={nameFontFamily}
              mobileFontFamily={mobileNameFontFamily}
              lineHeight={nameLineHeight}
              mobileLineHeight={mobileNameLineHeight}
              color={nameColor}
              mobileColor={mobileNameColor}
              coupon={coupon}
              discountAmount={pricePlanPricing.discount.amount}
              locale={locale}
            />
          )}
          {pricePlanPricing.tax.amount > 0 && (
            <Vat
              plan={plan}
              quantity={quantity}
              taxAmount={pricePlanPricing.tax.amount}
              taxPercent={pricePlanPricing.tax.rate}
              locale={locale}
              amountFontSize={amountFontSize}
              mobileAmountFontSize={mobileAmountFontSize}
              amountFontStyle={amountFontStyle}
              mobileAmountFontStyle={mobileAmountFontStyle}
              amountFontWeight={amountFontWeight}
              mobileAmountFontWeight={mobileAmountFontWeight}
              amountFontFamily={amountFontFamily}
              mobileAmountFontFamily={mobileAmountFontFamily}
              amountLineHeight={amountLineHeight}
              mobileAmountLineHeight={mobileAmountLineHeight}
              amountColor={amountColor}
              mobileAmountColor={mobileAmountColor}
              fontSize={nameFontSize}
              mobileFontSize={mobileNameFontSize}
              fontStyle={nameFontStyle}
              mobileFontStyle={mobileNameFontStyle}
              fontWeight={nameFontWeight}
              mobileFontWeight={mobileNameFontWeight}
              fontFamily={nameFontFamily}
              mobileFontFamily={mobileNameFontFamily}
              lineHeight={nameLineHeight}
              mobileLineHeight={mobileNameLineHeight}
              color={nameColor}
              mobileColor={mobileNameColor}
            />
          )}
          {needTotal && (
            <Total
              coupon={coupon}
              plan={plan}
              quantity={quantity}
              grossAmount={pricePlanPricing.grossAmount}
              locale={locale}
              amountFontSize={amountFontSize}
              mobileAmountFontSize={mobileAmountFontSize}
              amountFontStyle={amountFontStyle}
              mobileAmountFontStyle={mobileAmountFontStyle}
              amountFontWeight={amountFontWeight}
              mobileAmountFontWeight={mobileAmountFontWeight}
              amountFontFamily={amountFontFamily}
              mobileAmountFontFamily={mobileAmountFontFamily}
              amountLineHeight={amountLineHeight}
              mobileAmountLineHeight={mobileAmountLineHeight}
              amountColor={amountColor}
              mobileAmountColor={mobileAmountColor}
              fontSize={nameFontSize}
              mobileFontSize={mobileNameFontSize}
              fontStyle={nameFontStyle}
              mobileFontStyle={mobileNameFontStyle}
              fontWeight={nameFontWeight}
              mobileFontWeight={mobileNameFontWeight}
              fontFamily={nameFontFamily}
              mobileFontFamily={mobileNameFontFamily}
              lineHeight={nameLineHeight}
              mobileLineHeight={mobileNameLineHeight}
              color={nameColor}
              mobileColor={mobileNameColor}
            />
          )}
        </>
      )}
    </PricePlanUi>
  )
}

export const ReadablePricePlanAmount = ({
  plan,
  amount,
  locale,
}: {
  plan: PricePlanType
  amount: number
  locale: string
}) => {
  const amountWithCurrency = getReadablePrice(
    centsToEuro(amount),
    plan.currency,
    locale,
  )

  switch (plan.type) {
    case PricePlanTypeEnum.OneShot:
      return <>{amountWithCurrency}</>
    case PricePlanTypeEnum.LimitedSubscription: {
      return (
        <LimitedSubscriptionPlan
          trialInterval={plan.subscriptionPlan.trialInterval}
          trialPeriod={plan.subscriptionPlan.trialPeriod}
          interval={plan.subscriptionPlan.interval}
          intervalCount={plan.subscriptionPlan.intervalCount}
          limitOfPayments={plan.subscriptionPlan.limitOfPayments}
          amountWithCurrency={amountWithCurrency}
        />
      )
    }
    case PricePlanTypeEnum.Subscription:
      return (
        <SubscriptionPlan
          trialInterval={plan.subscriptionPlan.trialInterval}
          trialPeriod={plan.subscriptionPlan.trialPeriod}
          interval={plan.subscriptionPlan.interval}
          intervalCount={plan.subscriptionPlan.intervalCount}
          amountWithCurrency={amountWithCurrency}
        />
      )
    default:
      return <div />
  }
}

export default PricePlan
